import type { PermittedLocation } from '@cfa/pathway-fe-shared/xp';

import { env } from '@/env';

export const DEFAULT_REGULATED_STATES = ['CA'] as const;

/**
 * Parses `REACT_APP_REGULATED_STATES` (comma-separated US state codes).
 * Defaults to CA (California) when unset or empty.
 */
export const parseRegulatedStates = (
  raw: string | undefined,
  defaultStates: readonly string[] = DEFAULT_REGULATED_STATES,
): readonly string[] => {
  if (!raw?.trim()) {
    return defaultStates;
  }

  const states = raw
    .split(',')
    .map(state => state.trim().toUpperCase())
    .filter(Boolean);

  return states.length > 0 ? states : defaultStates;
};

/**
 * States where compliance-aware UX applies (plan description helper copy,
 * certificate footer, etc.).
 *
 * Production always defaults to `CA`; lower environments may widen for testing.
 */
export const getRegulatedStates = (
  appEnv: 'dev' | 'test' | 'prod',
  envOverride: string | undefined,
): readonly string[] =>
  appEnv === 'prod'
    ? DEFAULT_REGULATED_STATES
    : parseRegulatedStates(envOverride);

const regulatedStates = new Set(
  getRegulatedStates(env.REACT_APP_ENV, env.REACT_APP_REGULATED_STATES),
);

const hasPermittedLocationInStates = (
  states: ReadonlySet<string>,
  permittedLocations: PermittedLocation[] | undefined,
): boolean =>
  permittedLocations?.some(({ location }) => {
    const state = location.state?.toUpperCase();
    return state != null && states.has(state);
  }) ?? false;

/**
 * Returns true when the current user has any permitted location in a regulated state.
 *
 * Defaults to `CA` in prod. Non-prod may widen via `REACT_APP_REGULATED_STATES`.
 *
 * Note: This is the short-term heuristic, as the BE does not expose assignee locations
 * on certificate/plan payloads, so gating uses the current user's permitted locations.
 */
export const userHasRegulatedLocation = (
  permittedLocations: PermittedLocation[] | undefined,
): boolean => hasPermittedLocationInStates(regulatedStates, permittedLocations);
